import React, { useRef } from 'react';
import { useField } from 'formik';
import * as Yup from 'yup';
import styled from '@emotion/styled';

import { FormComponent } from '../../../../components/Form/FormComponent';
import { FieldErrorInfo } from '../../../../components/Form/FormField';
import SearchIcon from '../../../../components/Icons/SearchIcon';
import { LoadingOutlined } from '@ant-design/icons';
import JobFilterButton from '../JobFilterButton/JobFilterButton';
import { useJobSearch } from '../../../../context/JobSearchContext';

export interface IInputField {
  label?: string;
  id?: string;
  name: string;
  placeholder?: string;
  className?: string;
}

const CustomInputField: React.FC<IInputField> = ({
  label,
  className,
  ...props
}) => {
  const [field, meta] = useField(props);

  const { payInterval, location } = useJobSearch();

  const jobFilterButtonRef = useRef<HTMLDivElement>(null);

  const handleOpenJobFilter = () => {
    jobFilterButtonRef.current?.click();
  };

  return (
    <InputWrapper className={className}>
      {label && (
        <InputLabel htmlFor={props.name || props.id}>{label}</InputLabel>
      )}
      <InputContainer>
        <Input {...field} {...props} />
        <RightPlaceHolderCardIcon
          alt="icon"
          src="/static/svg/search-right-icon.svg"
          onClick={handleOpenJobFilter}
        />
        <LeftPlaceHolderCardIcon
          alt="icon"
          src="/static/svg/search-left-icon.svg"
        />
      </InputContainer>
      {meta.touched && meta.error && (
        <FieldErrorInfo>{meta.error}</FieldErrorInfo>
      )}
      {/* {!meta.error && field.value && show && (
        <FieldInfo>{`Showing results for: "${field.value}"`}</FieldInfo>
      )} */}
      <JobFilterButton ref={jobFilterButtonRef} />
      {((location && location.toLowerCase() !== 'any') ||
        (payInterval && payInterval.toLowerCase() !== 'any')) && (
        <FilterSummary>
          Filtering by--{' '}
          {location &&
            location.toLowerCase() !== 'any' &&
            `Location: ${location}`}
          {location &&
            location.toLowerCase() !== 'any' &&
            payInterval &&
            payInterval.toLowerCase() !== 'any' &&
            ' | '}
          {payInterval &&
            payInterval.toLowerCase() !== 'any' &&
            `Pay Interval: ${payInterval.toLocaleLowerCase()}`}
        </FilterSummary>
      )}
    </InputWrapper>
  );
};

const JobSearchSchema = Yup.object().shape({
  search: Yup.string(),
});

interface JobSearchFormProps {
  showMobileInfo: boolean;
}

const JobSearchForm: React.FC<JobSearchFormProps> = ({ showMobileInfo }) => {
  const {
    loading,
    keyword,
    setKeyword,
    setPage,
    setLocation,
    setPayInterval,
    payInterval,
    location,
  } = useJobSearch();

  // onSubmit now calls getLocumJobs with the search term
  const onSubmit = (values: any) => {
    setPage('1');
    setKeyword(values.search);
  };

  return (
    <Container className={showMobileInfo ? 'hideMobileScreen' : ''}>
      <FormComponent
        initialValues={{ search: keyword }}
        schema={JobSearchSchema}
        onSubmit={onSubmit}
      >
        <Box>
          {/* Web version: hidden on small screens */}
          <CustomInputField
            name="search"
            id="search"
            placeholder="Enter job title, keyword or company name"
            className="hideOnSmallScreen"
          />
          {/* Mobile version: hidden on large screens */}
          <CustomInputField
            name="search"
            id="search"
            placeholder="Search Jobs"
            className="hideOnLargeScreen"
          />
          <FormSubmitButton type="submit" className="hideOnSmallScreen search">
            {loading ? <LoadingOutlined /> : 'Find Jobs'}
          </FormSubmitButton>
          <FormSubmitButton type="submit" className="hideOnLargeScreen search">
            {loading ? <LoadingOutlined /> : <SearchIcon />}
          </FormSubmitButton>
          {keyword && (
            <SearchResultBanner>
              Showing results for “{keyword}”
            </SearchResultBanner>
          )}
          {(keyword || location || payInterval) && (
            <ResetButton
              type="button"
              onClick={() => {
                setPage('1');
                setKeyword('');
                setLocation('');
                setPayInterval('');
              }}
            >
              Reset
            </ResetButton>
          )}
        </Box>
      </FormComponent>
    </Container>
  );
};

export default JobSearchForm;

const Container = styled.div`
  /* border: 1px solid red; */
  position: sticky;
  top: 70px;
  z-index: 20;
  justify-content: center;
  margin-top: 32px;
  /* border: 1px solid red; */

  @media (max-width: 884px) {
    margin-top: 0px;

    &.hideMobileScreen {
      display: none;
    }
  }

  @media (max-width: 768px) {
    top: 90px;
  }
`;

const Box = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
  background-color: white;
  padding: 10px 30px;
  border-radius: 12px;
  /* border: 1px solid red; */

  @media (max-width: 768px) {
    padding: 0;
    background-color: #f2f8fd;
  }

  @media (max-width: 480px) {
    gap: 10px;
  }
`;

export const InputWrapper = styled.div`
  width: 100%;

  /*  */
  &.hideOnSmallScreen {
    @media (max-width: 480px) {
      display: none;
    }
  }

  &.hideOnLargeScreen {
    @media (min-width: 481px) {
      display: none;
    }
  }
`;

export const InputLabel = styled.label`
  font-size: 18px;
  font-weight: 400;
  color: ${({ theme }) => theme.palette.blackBlack2};
  white-space: nowrap;
  & > span {
    color: ${({ theme }) => theme.palette.greyGrey2};
    font-size: 15px;
    font-weight: 400;
  }
`;

export const InputContainer = styled.div`
  display: grid;
  height: fit-content;

  & > * {
    grid-column: 1;
    grid-row: 1;
  }
  &:hover {
    cursor: pointer;
  }
`;

export const Input = styled.input`
  border-radius: 4px;
  padding: 15px;

  padding-left: 62px;
  padding-right: 62px;

  text-align: left;
  font-weight: 300;
  color: ${({ theme }) => theme.palette.blackBlack2};
  font-size: 16px;
  border: 1.5px solid transparent;
  background-color: ${({ theme }) => theme.palette.greyGrey4};
  height: 45px;
  cursor: text;
  /* appearance: none; */

  &:focus {
    outline: none;
    border: 1.5px solid ${({ theme }) => theme.palette.mainBlue};
  }

  &:hover {
    cursor: text;
  }

  &::placeholder {
    color: ${({ theme }) => theme.palette.greyGrey3};
    font-size: 18px;
    font-weight: 300;
  }
  /* border: 1px solid red; */

  @media (max-width: 768px) {
    padding-left: 48px;
    padding-right: 48px;
    border: 1.5px solid ${({ theme }) => theme.palette.greyGrey3};
  }

  @media (max-width: 480px) {
    padding-left: 16px;
    padding-right: 43px;
  }
`;

const FormSubmitButton = styled.button`
  background: ${({ theme }) => theme.palette.mainBlue};
  font-weight: 600;
  font-size: 18px;
  color: white;
  border: none;
  cursor: pointer;
  width: fit-content;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.25rem;
  padding: 4px 38px;

  &.search {
    max-height: 2.8rem;
  }

  @media (max-width: 768px) {
    /* border: 1px solid red; */
    padding: 4px 14px;
  }

  @media (max-width: 480px) {
    font-weight: 500;
    font-size: 16px;
    padding: 4px 10px;
  }

  @media (max-width: 340px) {
    padding: 4px;
  }

  &.hideOnSmallScreen {
    @media (max-width: 480px) {
      display: none;
    }
  }

  &.hideOnLargeScreen {
    @media (min-width: 481px) {
      display: none;
    }
  }
`;

export const LeftPlaceHolderCardIcon = styled.img`
  display: grid;
  margin-left: 30px;
  margin-top: 1rem;
  place-content: center;
  margin-top: auto;
  margin-bottom: auto;

  width: 24px;
  height: 24px;

  @media (max-width: 768px) {
    margin-left: 16px;
  }

  @media (max-width: 480px) {
    display: none;
  }
`;

export const RightPlaceHolderCardIcon = styled.img`
  display: grid;
  margin-right: 30px;
  margin-left: auto;
  place-content: center;
  margin-top: auto;
  margin-bottom: auto;

  width: 24px;
  height: 24px;

  @media (max-width: 768px) {
    margin-right: 16px;
  }

  @media (max-width: 480px) {
    width: 22px;
    height: 22px;
  }
`;

const FilterSummary = styled.div`
  margin-top: 8px;
  font-size: 14px;
  color: #555;
`;
// const FieldInfo = styled.p`
//   font-size: 14px;
//   color: ${({ theme }) => theme.palette.greyGrey3};
// `;

const SearchResultBanner = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.palette.blackBlack2};
`;

const ResetButton = styled.button`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.palette.mainBlue};
  text-decoration: underline;
  cursor: pointer;
  font-size: 14px;
  margin-left: auto;
`;
